import { type ComboboxData, type SelectProps } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import api, { apiWithoutCamelization } from "../../../api";

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 200;

/**
 * Disables Mantine's client-side filtering so backend search results are shown
 * as-is. Required because the search term (e.g. a postal code) need not appear
 * in the option's label, which is all the default filter matches against.
 */
const showAllOptionsFilter: NonNullable<SelectProps["filter"]> = ({
  options
}) => options;

interface UseAsyncSelectDataParams {
  dataUrl: string;
  withoutCamelization?: boolean;
  labelField?: string;
  hasSelectedValue?: boolean;
}

/**
 * Manages search state and data fetching for the async select components.
 * Debounces the search term and forwards it to the backend (which can match by
 * name as well as e.g. postal code), instead of filtering the option labels
 * client-side. Returns `hasMinThreeCharsSearch` so callers can gate the
 * rendered options until enough characters are typed (or a value is selected).
 */
function useAsyncSelectData({
  dataUrl,
  withoutCamelization = false,
  labelField,
  hasSelectedValue = false
}: UseAsyncSelectDataParams) {
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue] = useDebouncedValue(
    searchValue,
    SEARCH_DEBOUNCE_MS
  );

  const search =
    debouncedSearchValue.length >= MIN_SEARCH_LENGTH
      ? debouncedSearchValue
      : undefined;

  const { data, isError, isLoading } = useQuery({
    queryKey: [
      "async-select-data",
      dataUrl,
      withoutCamelization,
      labelField,
      search
    ],
    queryFn: () =>
      fetchAsyncSelectData(dataUrl, withoutCamelization, labelField, search)
  });

  const hasMinThreeCharsSearch =
    hasSelectedValue || searchValue.length >= MIN_SEARCH_LENGTH;

  return {
    data: hasMinThreeCharsSearch ? data : [],
    hasMinThreeCharsSearch,
    isError,
    isLoading,
    searchValue,
    setSearchValue
  };
}

async function fetchAsyncSelectData(
  dataUrl: string,
  withoutCamelization: boolean,
  labelField?: string,
  search?: string
): Promise<ComboboxData> {
  const response = await (
    withoutCamelization ? apiWithoutCamelization : api
  ).get(dataUrl, { params: search ? { search } : undefined });
  return mantineifyData(response.data, labelField);
}

function mantineifyData(
  data: Array<{ name: string; id: number | string }>,
  labelField?: string
): ComboboxData {
  return data.map((dataEntry) => ({
    label: labelField ? dataEntry[labelField] : dataEntry.name,
    value: dataEntry.id.toString()
  }));
}

export { showAllOptionsFilter, useAsyncSelectData };
